import React, { useEffect, useState } from "react";
import { BookOpenText, X, Share2, Check, Image as ImageIcon, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { BRAND } from "@/lib/brand";

export default function ScriptureDialog() {
  const [verse, setVerse] = useState(null);
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [imaging, setImaging] = useState(false);

  useEffect(() => {
    if (sessionStorage.getItem("showScripture") !== "1") return;
    sessionStorage.removeItem("showScripture");
    api.get("/scripture/today").then((r) => {
      setVerse(r.data);
      setOpen(true);
    }).catch(() => {});
  }, []);

  const share = async () => {
    const shareText = `"${verse.text}" — ${verse.reference}\n\nvia In His Image Radio`;
    if (navigator.share) {
      try { await navigator.share({ title: "Scripture of the Day", text: shareText }); return; } catch (e) {}
    }
    try {
      await navigator.clipboard.writeText(shareText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {}
  };

  const wrapText = (ctx, text, maxWidth) => {
    const words = text.split(" ");
    const lines = [];
    let line = "";
    for (const w of words) {
      const test = line ? line + " " + w : w;
      if (ctx.measureText(test).width > maxWidth && line) { lines.push(line); line = w; }
      else line = test;
    }
    if (line) lines.push(line);
    return lines;
  };

  const shareImage = async () => {
    setImaging(true);
    try {
      const S = 1080;
      const canvas = document.createElement("canvas");
      canvas.width = S; canvas.height = S;
      const ctx = canvas.getContext("2d");
      // background gradient
      const g = ctx.createLinearGradient(0, 0, S, S);
      g.addColorStop(0, "#141C42"); g.addColorStop(1, "#0A1030");
      ctx.fillStyle = g; ctx.fillRect(0, 0, S, S);
      // gold border frame
      ctx.strokeStyle = "rgba(230,179,74,0.35)"; ctx.lineWidth = 4;
      ctx.strokeRect(50, 50, S - 100, S - 100);
      // header
      ctx.fillStyle = "#E6B34A";
      ctx.font = "600 30px Georgia, serif";
      ctx.textAlign = "center";
      ctx.fillText("DAILY DEVOTIONAL", S / 2, 170);
      // verse text
      ctx.fillStyle = "#FFFFFF";
      ctx.font = "700 60px Georgia, serif";
      const lines = wrapText(ctx, `"${verse.text}"`, S - 220);
      const lineH = 82;
      const startY = S / 2 - (lines.length * lineH) / 2;
      lines.forEach((ln, i) => ctx.fillText(ln, S / 2, startY + i * lineH));
      // reference
      ctx.fillStyle = "#E6B34A";
      ctx.font = "italic 700 44px Georgia, serif";
      ctx.fillText(verse.reference, S / 2, startY + lines.length * lineH + 60);
      // brand footer
      ctx.fillStyle = "rgba(255,255,255,0.6)";
      ctx.font = "500 30px Georgia, serif";
      ctx.fillText(BRAND, S / 2, S - 110);

      const blob = await new Promise((res) => canvas.toBlob(res, "image/png"));
      const file = new File([blob], "scripture.png", { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: "Scripture of the Day", text: `${verse.reference} — via ${BRAND}` });
      } else {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = `scripture-${verse.reference.replace(/[^a-z0-9]+/gi, "-")}.png`;
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch (e) {} finally { setImaging(false); }
  };

  if (!open || !verse) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-6" data-testid="scripture-overlay">
      <div className="absolute inset-0 bg-[#050818]/80 backdrop-blur-sm" onClick={() => setOpen(false)} />
      <div className="relative w-full max-w-lg rounded-3xl border border-[#E6B34A]/20 bg-gradient-to-b from-[#141C42] to-[#0E1536] p-8 lg:p-10 shadow-[0_0_80px_rgba(230,179,74,0.18)] animate-in fade-in zoom-in-95 duration-300" data-testid="scripture-dialog">
        <button onClick={() => setOpen(false)} data-testid="scripture-close" className="absolute top-4 right-4 p-2 rounded-lg text-zinc-500 hover:text-white hover:bg-white/5 transition-colors">
          <X className="w-4 h-4" />
        </button>
        <div className="flex items-center gap-2 mb-6">
          <BookOpenText className="w-5 h-5 text-[#E6B34A]" />
          <span className="overline text-[#E6B34A]">Daily Devotional</span>
        </div>
        <blockquote className="font-display font-black text-2xl lg:text-3xl tracking-tight leading-tight text-white" data-testid="scripture-text">
          &ldquo;{verse.text}&rdquo;
        </blockquote>
        <p className="text-[#E6B34A] font-semibold mt-4" data-testid="scripture-ref">{verse.reference}</p>

        {verse.reflection && (
          <div className="mt-6 rounded-2xl bg-white/[0.03] border border-white/5 p-5" data-testid="scripture-reflection">
            <p className="overline text-zinc-500 mb-2">Reflection</p>
            <p className="text-sm text-zinc-300 leading-relaxed">{verse.reflection}</p>
          </div>
        )}

        <div className="flex gap-3 mt-8">
          <button onClick={share} data-testid="scripture-share" className="flex-1 h-12 rounded-xl bg-white/5 hover:bg-white/10 text-white font-semibold transition-colors flex items-center justify-center gap-2">
            {copied ? <><Check className="w-4 h-4 text-emerald-400" /> Copied</> : <><Share2 className="w-4 h-4" /> Share text</>}
          </button>
          <button onClick={shareImage} disabled={imaging} data-testid="scripture-share-image" className="flex-1 h-12 rounded-xl bg-white/5 hover:bg-white/10 text-white font-semibold transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
            {imaging ? <Loader2 className="w-4 h-4 animate-spin" /> : <><ImageIcon className="w-4 h-4" /> Share image</>}
          </button>
        </div>
        <button onClick={() => setOpen(false)} data-testid="scripture-amen" className="mt-3 w-full h-12 rounded-xl bg-[#E6B34A] text-[#0A1030] font-bold hover:bg-[#CF9A34] transition-colors">
          Amen 🙏
        </button>
      </div>
    </div>
  );
}
